// Cómo se resume un ticket en una fila: cuál línea encabeza, cómo se nombra y qué queda en «+N».
// La venta sin teléfono (sku null) encabeza su primer producto físico, no el servicio. Puro.
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    extraItemsLabel,
    PRODUCT_CATEGORY_LABEL,
    SERVICE_CATEGORY,
    SERVICE_CATEGORY_LABEL,
} from '@/shared/lib/format/productCategory';

/** Llave de la categoría de una línea sin producto, igual a la de los filtros de las fichas. */
export const UNCATEGORIZED_LINE = 'UNCATEGORIZED';

/** Categoría de una línea: «Servicio», la del catálogo o «Sin categoría». */
export type LineCategoryKey =
    | `${ProductCategoryEnum}`
    | typeof SERVICE_CATEGORY
    | typeof UNCATEGORIZED_LINE;

/** Categoría con la que filtran las fichas; `null` = sin filtro. */
export type TicketCategory = string | null;

/** Encabezado de la venta sin equipo. */
export const NO_PHONE_LABEL = 'Sin teléfono';

/** Lo mínimo de una venta para resumir su ticket; sin teléfono, el sku viene en `null`. */
export interface TicketSale {
    sku?: string | null;
    lines?: SaleLine[];
    /** Resumen sin líneas de la venta sin teléfono: nombre del accesorio principal. */
    mainAccessoryName?: string;
    /** Resumen sin líneas: cuántas líneas no regalo hay además de la principal. */
    extraLinesCount?: number;
}

/** La venta trae equipo: un sku con contenido. */
export function hasEquipment<TSale extends Pick<TicketSale, 'sku'>>(
    sale: TSale,
): sale is TSale & { sku: string } {
    return (sale.sku?.trim() ?? '') !== '';
}

/** Nombre del equipo de la venta: marca y modelo; si no, su SKU; si no, «Sin nombre». */
export function saleProductName(sale: {
    sku?: string | null;
    productBrand?: string | null;
    productModel?: string | null;
}): string {
    return itemDisplayName({ sku: sale.sku, brand: sale.productBrand, model: sale.productModel });
}

/** Único lugar que decide la categoría de una línea: un servicio nunca cuenta como accesorio. */
export function lineCategoryKey(line: SaleLine): LineCategoryKey {
    if (line.product?.itemType === 'SERVICE') return SERVICE_CATEGORY;
    return line.product?.category ?? UNCATEGORIZED_LINE;
}

export function lineCategoryLabel(category: LineCategoryKey): string {
    if (category === UNCATEGORIZED_LINE) return 'Sin categoría';
    if (category === SERVICE_CATEGORY) return SERVICE_CATEGORY_LABEL;
    return PRODUCT_CATEGORY_LABEL[category];
}

export function lineMatchesCategory(line: SaleLine, category: TicketCategory): boolean {
    return category !== null && lineCategoryKey(line) === category;
}

export function saleLineName(line: SaleLine): string {
    return itemDisplayName({ sku: line.sku, ...line.product });
}

/** Las líneas del ticket; `null` en las ventas anteriores a las líneas. */
export function ticketLines(sale: Pick<TicketSale, 'lines'>): SaleLine[] | null {
    return sale.lines && sale.lines.length > 0 ? sale.lines : null;
}

/** Las líneas que se despliegan: 2+ con teléfono; sin teléfono, aun una sola. */
export function expandableTicketLines(sale: TicketSale): SaleLine[] | null {
    const minLines = hasEquipment(sale) ? 2 : 1;
    return sale.lines && sale.lines.length >= minLines ? sale.lines : null;
}

/** Línea de un producto físico: sin itemType cuenta como producto. */
function isPhysicalLine(line: SaleLine): boolean {
    return line.product?.itemType !== 'SERVICE';
}

/**
 * Encabeza la de la categoría filtrada; si no, la del SKU principal; si no, el primer producto
 * físico sin regalo; si no hay, la primera sin regalo (el servicio).
 */
export function headlineLine(
    lines: readonly SaleLine[],
    options: { category: TicketCategory; mainSku?: string | null },
): SaleLine {
    const notGift = (line: SaleLine) => line.role !== 'GIFT';
    const inCategory = lines.filter((line) => lineMatchesCategory(line, options.category));
    return (
        inCategory.find(notGift) ??
        inCategory.at(0) ??
        lines.find((line) => options.mainSku && line.sku === options.mainSku && notGift(line)) ??
        lines.find((line) => notGift(line) && isPhysicalLine(line)) ??
        lines.find(notGift) ??
        lines[0]
    );
}

/** «+1 accesorio» si las demás líneas son de una sola categoría; si se mezclan, solo «+N». */
export function extraLinesLabel(extras: readonly SaleLine[]): string {
    return extraItemsLabel(
        extras.map((line) => {
            const key = lineCategoryKey(line);
            return key === UNCATEGORIZED_LINE ? null : key;
        }),
    );
}

export interface TicketHeadline {
    /** `null` cuando el ticket no se despliega (sin líneas o con una). */
    line: SaleLine | null;
    extras: SaleLine[];
    name: string;
    /** `null` en la venta sin teléfono ni líneas. */
    sku: string | null;
    /** La venta no trae equipo: la fila se encabeza «Sin teléfono». */
    noPhone: boolean;
    /** Las demás líneas que se cuentan en «+N» cuando el ticket no trae líneas. */
    extraCount: number;
}

/** Nombre de la línea: la del SKU principal conserva el de la venta. */
function lineName(line: SaleLine, sale: TicketSale, saleName: string): string {
    return hasEquipment(sale) && line.sku === sale.sku ? saleName : saleLineName(line);
}

/**
 * Lo que dice la celda Producto, que también la ordena. Sin despliegue, el nombre de la venta (o
 * «Sin teléfono»); la línea del SKU principal conserva ese nombre y las demás usan el suyo.
 */
export function ticketHeadline(
    sale: TicketSale,
    category: TicketCategory,
    saleName: string,
): TicketHeadline {
    const noPhone = !hasEquipment(sale);
    const lines = expandableTicketLines(sale);
    if (!lines) return headlineWithoutLines(sale, saleName);
    const line = headlineLine(lines, { category, mainSku: sale.sku });
    return {
        line,
        extras: lines.filter((other) => other !== line),
        name: lineName(line, sale, saleName),
        sku: line.sku,
        noPhone,
        extraCount: 0,
    };
}

/** Sin líneas: el nombre de la venta; sin teléfono, su resumen de accesorio o «Sin teléfono». */
function headlineWithoutLines(sale: TicketSale, saleName: string): TicketHeadline {
    if (hasEquipment(sale)) {
        return {
            line: null,
            extras: [],
            name: saleName,
            sku: sale.sku,
            noPhone: false,
            extraCount: 0,
        };
    }
    const accessory = sale.mainAccessoryName?.trim();
    return {
        line: null,
        extras: [],
        name: accessory || NO_PHONE_LABEL,
        sku: null,
        noPhone: true,
        extraCount: accessory ? (sale.extraLinesCount ?? 0) : 0,
    };
}

/** Título de la celda: las líneas con su SKU (tras «Sin teléfono» si no hay equipo). */
export function ticketTitle(sale: TicketSale, saleName: string): string {
    const lines = expandableTicketLines(sale);
    const noPhone = !hasEquipment(sale);
    if (!lines) return noPhone ? NO_PHONE_LABEL : `${saleName} · ${sale.sku}`;
    const listed = lines
        .map((line) => `${lineName(line, sale, saleName)} · ${line.sku}`)
        .join(' + ');
    return noPhone ? `${NO_PHONE_LABEL}: ${listed}` : listed;
}
